import { useState } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { ProjectsScreen } from './pages/Projects';
import { ProjectDetailScreen } from './pages/ProjectDetail';
import { LoginScreen } from './pages/Login';
import { CreateSiteScreen } from './pages/CreateSite';
import { DomainsScreen } from './pages/Domains';
import { SettingsScreen } from './pages/Settings';
import { NavBar } from './components/NavBar';
import { useSession } from './contexts/SessionContext';
import type { Tab, View } from './routes';
import { tabFromView, viewFromTab } from './routes';

export function App() {
  const { loading, user } = useSession();
  const [view, setView] = useState<View>({ kind: 'list' });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-zinc-400">
          <div className="h-6 w-6 rounded-full border-2 border-zinc-200 border-t-zinc-900 animate-spin" />
          <span className="text-[12px] tracking-tightish">Loading</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginScreen />;
  }

  const tab: Tab = tabFromView(view);
  const onTabChange = (t: Tab) => setView(viewFromTab(t));

  return (
    <Tooltip.Provider delayDuration={300} skipDelayDuration={150}>
      <div className="min-h-screen flex flex-col">
        <NavBar tab={tab} onTabChange={onTabChange} onHome={() => setView({ kind: 'list' })} />
        <main className="flex-1 mx-auto w-full max-w-6xl px-4 sm:px-6 py-6 sm:py-10 animate-fade-in">
          {view.kind === 'list' && (
            <ProjectsScreen
              onOpen={(id) => setView({ kind: 'detail', projectId: id })}
              onCreate={() => setView({ kind: 'create' })}
            />
          )}
          {view.kind === 'create' && (
            <CreateSiteScreen
              onCreated={(id) => setView({ kind: 'detail', projectId: id })}
              onCancel={() => setView({ kind: 'list' })}
            />
          )}
          {view.kind === 'detail' && (
            <ProjectDetailScreen
              projectId={view.projectId}
              onBack={() => setView({ kind: 'list' })}
              onGoToDomains={() => setView({ kind: 'domains' })}
            />
          )}
          {view.kind === 'domains' && <DomainsScreen />}
          {view.kind === 'settings' && <SettingsScreen />}
        </main>
        <footer className="py-6 text-center text-[11px] text-zinc-400">
          Sitekit Factory · v0.1
        </footer>
      </div>
    </Tooltip.Provider>
  );
}
